import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { KeyRound, Phone, MessageCircle } from "lucide-react";
import { ops, apiError, fmtDate, fmtDT } from "./opsApi";
import { inputCls, Field, Btn, Badge, Card, CardTitle, Empty, Msg } from "./opsUi";

const EvalRow = ({ s }) => (
  <div className="rounded-xl bg-dwc-soft/60 p-3">
    <p className="text-sm font-bold text-dwc-charcoal">{fmtDate(s.date)} · {s.time} <Badge value={s.status} /> <span className="text-[11px] text-dwc-charcoal/50">Trainer: {s.trainer_name}</span></p>
    {s.evaluation && (
      <div className="mt-1 text-[11px] font-semibold text-dwc-charcoal/70">
        <p>Confidence: <b>{s.evaluation.confidence}</b> · Panic: <b>{s.evaluation.panic}</b> · Road awareness: <b>{String(s.evaluation.road_awareness).replace("_", " & ")}</b></p>
        {s.evaluation.repeated_mistakes && <p>Working on: {s.evaluation.repeated_mistakes}</p>}
        {s.evaluation.remarks && <p>Trainer remarks: {s.evaluation.remarks}</p>}
        {s.evaluation.next_focus && <p className="font-extrabold text-dwc-green">Next session focus: {s.evaluation.next_focus}</p>}
      </div>
    )}
  </div>
);

export default function StudentDashboard() {
  const [data, setData] = useState(null);
  const [fb, setFb] = useState({ type: "feedback", description: "" });
  const [msg, setMsg] = useState(null);
  const load = useCallback(() => ops.get("/student/overview").then(setData).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const sendFeedback = async (e) => {
    e.preventDefault();
    try {
      await ops.post("/student/feedback", fb);
      setMsg({ ok: true, text: "Thank you! DWC has received your message." });
      setFb({ type: "feedback", description: "" });
    } catch (err) { setMsg({ ok: false, text: apiError(err) }); }
    setTimeout(() => setMsg(null), 5000);
  };

  if (!data) return <Empty>Loading your training data...</Empty>;
  const pct = data.package.total ? Math.min(100, (data.package.completed / data.package.total) * 100) : 0;

  return (
    <div className="space-y-5" data-testid="student-dashboard">
      {data.active_otp && (
        <div className="rounded-2xl border-2 border-dwc-green bg-dwc-soft p-5 text-center" data-testid="student-otp-banner">
          <p className="text-[11px] font-extrabold uppercase tracking-wide text-dwc-charcoal/60">
            <KeyRound className="mr-1 inline h-3.5 w-3.5" />Session {data.active_otp.purpose === "start" ? "START" : "END"} code — share with your trainer only
          </p>
          <p className="mt-1 text-4xl font-black tracking-[0.3em] text-dwc-green" data-testid="student-otp-code">{data.active_otp.code}</p>
          <p className="mt-1 text-[11px] font-bold text-dwc-charcoal/45">
            For your session on {fmtDate(data.active_otp.session_date)} at {data.active_otp.session_time} · valid until {fmtDT(data.active_otp.expires)} · also sent on WhatsApp
          </p>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        <Card testId="student-profile-card">
          <CardTitle>My Profile</CardTitle>
          <p className="text-lg font-black text-dwc-charcoal">{data.profile.name}</p>
          <p className="text-xs font-bold text-dwc-charcoal/55">{data.profile.mobile}</p>
          {data.profile.package_name && <p className="mt-1 text-xs font-bold text-dwc-green">{data.profile.package_name}</p>}
          <div className="mt-3 border-t border-gray-100 pt-3">
            <p className="text-[11px] font-extrabold uppercase text-dwc-charcoal/50">My Trainer</p>
            {data.trainer ? (
              <p className="text-sm font-black text-dwc-charcoal">{data.trainer.name} <a href={`tel:+91${data.trainer.mobile}`} className="ml-1 text-dwc-green" data-testid="student-trainer-call"><Phone className="inline h-3.5 w-3.5" /></a></p>
            ) : <p className="text-xs font-semibold text-dwc-charcoal/50">Trainer will be assigned soon.</p>}
          </div>
        </Card>

        <Card testId="student-package-card">
          <CardTitle>Package Progress</CardTitle>
          <div className="flex items-end gap-2">
            <p className="text-4xl font-black text-dwc-green">{data.package.completed}</p>
            <p className="pb-1 text-sm font-bold text-dwc-charcoal/50">/ {data.package.total || "—"} sessions done</p>
          </div>
          <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-gray-100">
            <div className="h-full rounded-full bg-dwc-green transition-all" style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-2 text-xs font-extrabold text-dwc-charcoal/60">{data.package.remaining} sessions remaining</p>
        </Card>

        <Card testId="student-tips-card">
          <CardTitle>My Improvement Tips</CardTitle>
          <ul className="space-y-1.5">
            {data.tips.map((t, i) => (
              <li key={i} className="flex gap-2 text-sm font-semibold text-dwc-charcoal/75">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-dwc-green" />{t}
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <Card testId="student-upcoming-card">
        <CardTitle>Upcoming Sessions</CardTitle>
        {data.upcoming.length === 0 && <Empty>No upcoming sessions scheduled yet.</Empty>}
        <div className="space-y-2">
          {data.upcoming.map((s) => (
            <p key={s.id} className="text-sm font-bold text-dwc-charcoal">{fmtDate(s.date)} · {s.time} — with {s.trainer_name} <Badge value={s.status} /></p>
          ))}
        </div>
      </Card>

      <Card testId="student-completed-card">
        <CardTitle>Completed Sessions & Progress Reports ({data.completed.length})</CardTitle>
        {data.completed.length === 0 && <Empty>Your completed sessions will appear here.</Empty>}
        <div className="space-y-2.5">{data.completed.map((s) => <EvalRow key={s.id} s={s} />)}</div>
      </Card>

      <Card testId="student-materials-card">
        <CardTitle>Driving Lessons & Learning Material</CardTitle>
        <div className="grid gap-3 sm:grid-cols-2">
          {data.materials.map((m) => (
            <Link key={m.url} to={m.url} className="rounded-xl border border-gray-100 p-3 transition-colors hover:border-dwc-green/40 hover:bg-dwc-soft/50" data-testid={`material-${m.url.replace(/\//g, "")}`}>
              <p className="text-sm font-black text-dwc-charcoal">{m.title}</p>
              <p className="text-[11px] font-semibold text-dwc-charcoal/55">{m.desc}</p>
            </Link>
          ))}
        </div>
      </Card>

      <Card testId="student-feedback-card">
        <CardTitle>Feedback / Complaint</CardTitle>
        <form onSubmit={sendFeedback} className="space-y-3" data-testid="student-feedback-form">
          <Field label="Type">
            <select value={fb.type} onChange={(e) => setFb((v) => ({ ...v, type: e.target.value }))} className={inputCls} data-testid="feedback-type-select">
              <option value="feedback">Feedback</option>
              <option value="complaint">Complaint</option>
            </select>
          </Field>
          <Field label="Message">
            <textarea value={fb.description} onChange={(e) => setFb((v) => ({ ...v, description: e.target.value }))} rows={3} className={inputCls} required minLength={5} data-testid="feedback-desc-input" />
          </Field>
          <Msg msg={msg} />
          <div className="flex flex-wrap items-center gap-3">
            <Btn type="submit" testId="feedback-submit-btn">Send to DWC</Btn>
            <a href={`https://wa.me/${data.support.whatsapp}`} target="_blank" rel="noopener noreferrer" data-testid="student-support-whatsapp"
              className="inline-flex items-center gap-1.5 text-xs font-extrabold text-dwc-green">
              <MessageCircle className="h-4 w-4" />Chat with DWC Support
            </a>
            <span className="text-xs font-bold text-dwc-charcoal/50">Existing customer line: {data.support.existing_customer}</span>
          </div>
        </form>
      </Card>
    </div>
  );
}
